import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import { backgroundRect, getAnimDuration, getPageImage, imageAttrs } from "./layout"
import { useImageSize } from "../Storefront/utils/images"

/**
 * Illustrated page background: the image sits at its end rect and a Web
 * Animation carries it from the start rect (translate/scale/rotate) over the
 * page's animation duration. The animation is kept in sync with the engine's
 * page clock (pause/play, seeks, playback rate).
 */
export default function Background({ page, viewport, engine, state }) {
  const imgRef = useRef(null)
  const animRef = useRef(null)
  const [loaded, setLoaded] = useState(false)
  const image = getPageImage(page)
  const attrs = imageAttrs(image)
  const size = useImageSize(image)
  const end = backgroundRect(viewport, page, "end", size)
  const start = backgroundRect(viewport, page, "start", size)
  const duration = getAnimDuration(page)
  const { pageIndex, seekId, paused, rate, elapsed, transition } = state
  // The move is played as one animation from the start rect to the end one.
  // A reader never changes those while a page is on screen; an author does,
  // every time a keyframe is dragged, so the animation is rebuilt whenever
  // either end of it moves — otherwise the picture keeps playing the move it
  // was given when the page opened.
  const shape = `${start?.left},${start?.bottom},${start?.width},${start?.height},${start?.angle}|${end?.left},${end?.bottom},${end?.width},${end?.height},${end?.angle}|${duration}`

  useLayoutEffect(() => {
    setLoaded(false)
  }, [pageIndex, image])

  // (Re)create the animation when the page, the viewport or a seek changes
  useLayoutEffect(() => {
    const el = imgRef.current
    animRef.current?.cancel()
    animRef.current = null
    if (!el || !end || !start || !el.animate) return
    const scale = start.width / end.width
    const dx = start.left + start.width / 2 - (end.left + end.width / 2)
    const dy = -(start.bottom + start.height / 2 - (end.bottom + end.height / 2))
    const from = `translate(${dx}px, ${dy}px) scale(${scale}) rotate(${start.angle}deg)`
    const to = `translate(0px, 0px) scale(1) rotate(${end.angle}deg)`
    if (duration <= 0) {
      el.style.transform = to
      return
    }
    el.style.transform = ""
    const anim = el.animate([{ transform: from }, { transform: to }], {
      duration: duration * 1000,
      easing: "linear",
      fill: "forwards",
    })
    anim.currentTime = Math.min(engine.state.elapsed, duration) * 1000
    anim.playbackRate = rate
    if (paused) anim.pause()
    animRef.current = anim
    return () => anim.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, seekId, viewport.width, viewport.height, image, size.width, size.height, shape])

  useEffect(() => {
    const anim = animRef.current
    if (!anim) return
    if (paused) anim.pause()
    else anim.play()
  }, [paused, seekId])

  useEffect(() => {
    if (animRef.current) animRef.current.playbackRate = rate
  }, [rate])

  // Drift guard: keep the animation within half a second of the page clock
  useEffect(() => {
    const anim = animRef.current
    if (!anim || paused || duration <= 0) return
    const target = Math.min(elapsed, duration) * 1000
    if (Math.abs((anim.currentTime || 0) - target) > 500) anim.currentTime = target
  }, [elapsed, paused, duration])

  if (!attrs || !end) return null

  const fade = transition && page.transition && page.transition !== "Instant"
  return (
    <img
      ref={imgRef}
      key={pageIndex}
      src={attrs.src}
      srcSet={attrs.srcSet}
      sizes="100vw"
      alt=""
      draggable={false}
      onLoad={() => setLoaded(true)}
      className="sf-player-bg absolute select-none pointer-events-none max-w-none"
      style={{
        left: end.left,
        bottom: end.bottom,
        width: end.width,
        height: end.height,
        transformOrigin: "center center",
        opacity: loaded ? 1 : 0,
        transition: fade ? "opacity 0.5s" : "none",
      }}
    />
  )
}
